import * as React from 'react'
import { useCallback, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Coffee, Moon, Office, Salad } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import { Expensable } from 'modules/expenses/types/Expensable'

interface Props {
  expensable?: Expensable
  onConceptsChange?: (concepts: LocalConcept[]) => void
  disabled?: boolean
}

type ConceptField =
  | 'breakfastProvided'
  | 'lunchProvided'
  | 'dinnerProvided'
  | 'accommodationProvided'

export interface LocalConcept {
  id: number
  date: string
  breakfastProvided: boolean
  lunchProvided: boolean
  dinnerProvided: boolean
  accommodationProvided: boolean
}

interface ConceptIconProps {
  isProvided: boolean
  icon: typeof Coffee
  label: string
  onClick?: () => void
  disabled?: boolean
}

const ConceptIcon = ({
  isProvided,
  icon: IconComponent,
  label,
  onClick,
  disabled = false,
}: ConceptIconProps) => (
  <div
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => !disabled && e.key === 'Enter' && onClick?.()}
    style={{
      flex: '1 1 0',
      minWidth: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <Box
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="s4"
      padding="s8"
      borderRadius={{ all: 'abs008' }}
      background={disabled ? 'grey100' : isProvided ? 'viridian100' : 'grey100'}
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: disabled ? 'grey200' : isProvided ? 'viridian600' : 'grey300',
        },
      }}
    >
      <F0Icon
        icon={IconComponent}
        size="md"
        color={disabled ? 'secondary' : isProvided ? 'default' : 'secondary'}
      />
      <Text.Normal
        size="100"
        color={disabled ? 'grey400' : isProvided ? 'viridian1000' : 'grey600'}
      >
        {label}
      </Text.Normal>
    </Box>
  </div>
)

interface DayRowProps {
  concept: LocalConcept
  dayNumber: number
  onToggle: (conceptId: number, field: ConceptField) => void
  isLastDay: boolean
  disabled?: boolean
}

const DayRow = ({ concept, dayNumber, onToggle, isLastDay, disabled = false }: DayRowProps) => {
  const formattedDate = moment(concept.date).format('ddd, MMM D')

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {formattedDate}
        </Text.Normal>
        <Text.Normal size="200" color="grey600">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.day_label', {
            day: dayNumber,
          })}
        </Text.Normal>
      </Box>
      <div style={{ display: 'flex', gap: '8px' }}>
        <ConceptIcon
          isProvided={concept.breakfastProvided}
          icon={Coffee}
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.breakfast')}
          onClick={() => onToggle(concept.id, 'breakfastProvided')}
          disabled={disabled}
        />
        <ConceptIcon
          isProvided={concept.lunchProvided}
          icon={Salad}
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.lunch')}
          onClick={() => onToggle(concept.id, 'lunchProvided')}
          disabled={disabled}
        />
        <ConceptIcon
          isProvided={concept.dinnerProvided}
          icon={Moon}
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.dinner')}
          onClick={() => onToggle(concept.id, 'dinnerProvided')}
          disabled={disabled}
        />
        <ConceptIcon
          isProvided={concept.accommodationProvided}
          icon={Office}
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.overnight')}
          onClick={() => onToggle(concept.id, 'accommodationProvided')}
          disabled={disabled || isLastDay}
        />
      </div>
    </Box>
  )
}

const DailyConcepts = (props: Props) => {
  const { expensable, onConceptsChange, disabled = false } = props
  const perDiem = expensable?.perDiem
  const serverConcepts = perDiem?.perDiemConcepts || []

  // Local state for editing
  const [localConcepts, setLocalConcepts] = useState<LocalConcept[]>([])

  // Serialize concepts for stable dependency comparison
  const conceptsKey = serverConcepts
    .map((c) => c.id)
    .sort()
    .join(',')

  // Initialize local state from server data
  useEffect(() => {
    if (serverConcepts.length > 0) {
      const mapped: LocalConcept[] = serverConcepts.map((c) => ({
        id: c.id,
        date: c.date,
        breakfastProvided: c.breakfastProvided,
        lunchProvided: c.lunchProvided,
        dinnerProvided: c.dinnerProvided,
        accommodationProvided: c.accommodationProvided,
      }))
      const sorted = mapped.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      setLocalConcepts(sorted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptsKey])

  const handleToggle = useCallback(
    (conceptId: number, field: ConceptField) => {
      setLocalConcepts((prev) => {
        const updated = prev.map((concept) =>
          concept.id === conceptId ? { ...concept, [field]: !concept[field] } : concept
        )
        // Notify parent of changes asynchronously to avoid "setState during render" warning
        setTimeout(() => {
          onConceptsChange?.(updated)
        }, 0)
        return updated
      })
    },
    [onConceptsChange]
  )

  if (localConcepts.length === 0) {
    return null
  }

  return (
    <FormBlock
      title={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.title')}
      subtitle={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.description')}
    >
      <Box flexDirection="column" gap="s16">
        {localConcepts.map((concept, index) => (
          <DayRow
            key={concept.id}
            concept={concept}
            dayNumber={index + 1}
            onToggle={handleToggle}
            isLastDay={index === localConcepts.length - 1}
            disabled={disabled}
          />
        ))}
      </Box>
    </FormBlock>
  )
}

export default DailyConcepts
